import React, { useMemo, useState } from 'react';
import { usePostV1AuthMe } from '@/generated/endpoints';
import { LOCAL_STORAGE_KEYS } from '@/lib/constants';
import logger from '@/lib/logger';
import { SessionStatus } from '@/lib/types';

interface AuthenticationContextType {
  status: SessionStatus;
  customerId: number | null;
  jobIds: string[] | null;
  mainJobId: string | null;
  email: string | null;
}

const AuthenticationContext = React.createContext<AuthenticationContextType>({
  status: SessionStatus.LOADING,
  customerId: null,
  jobIds: null,
  mainJobId: null,
  email: null,
});

export function useAuth() {
  const context = React.useContext(AuthenticationContext);
  if (!context) {
    throw new Error('useAuth must be used within a AuthenticationProvider');
  }
  return context;
}

/**
 * Resolves the paperwork session once per page load. The token comes from the SMS link (`?t=`) or local storage and
 * is sent by the REST fetcher; the session details are kept in local storage for the journey hooks.
 */
export function AuthenticationProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>(SessionStatus.LOADING);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [jobIds, setJobIds] = useState<string[] | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [mainJobId, setMainJobId] = useState<string | null>(null);
  const { mutateAsync } = usePostV1AuthMe();

  React.useEffect(() => {
    if (status !== SessionStatus.LOADING) {
      return;
    }

    const signOut = () => {
      setStatus(SessionStatus.UNAUTHENTICATED);
      setCustomerId(null);
      setJobIds(null);
      setEmail(null);
      setMainJobId(null);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.ackPaperworkToken);
    };

    mutateAsync()
      .then((response) => {
        if (!response.success || response.data.kind !== 'PAPERWORK') {
          signOut();
          return;
        }
        const session = response.data.data;
        setCustomerId(session.cid);
        setJobIds(session.jobIds);
        setMainJobId(session.mainJobId);
        setEmail(session.customer.email);
        setStatus(SessionStatus.AUTHENTICATED);
      })
      .catch((error) => {
        logger.error('Authentication error:', error);
        signOut();
      });
  }, [mutateAsync, status]);

  const value = useMemo(
    () => ({ status, customerId, jobIds, email, mainJobId }),
    [status, customerId, jobIds, email, mainJobId],
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
}
